import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useT } from '@/lib/i18n';
import { useState } from 'react';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useOrgId } from '@/lib/org';
import { errorText, type Approval } from '@/lib/portal';
import type { TFunction } from 'i18next';
import { DecisionDialog } from './decision-dialog';

const docName = (a: Approval, t: TFunction) =>
  t(a.document.kind === 'invoice' ? 'approvals.docInvoice' : 'approvals.docBill', {
    number: a.document.number ?? '',
    name: a.document.contactName,
  }).replace(/\s{2,}/g, ' ');

/** Approve, Reject and Withdraw for one request (queue rows and the document strip). */
export function ApprovalActions({
  a,
  size = 'sm',
  onDone,
}: {
  a: Approval;
  size?: 'sm' | 'default';
  onDone?: () => void;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const t = useT();
  const act = useMutation({
    mutationFn: ({
      action,
      comment,
    }: {
      action: 'approve' | 'reject' | 'withdraw';
      comment?: string;
    }) =>
      api<Approval>(`/orgs/${orgId}/approvals/${a.id}/${action}`, {
        method: 'POST',
        body: JSON.stringify(comment ? { comment } : {}),
      }),
    onSuccess: async (r, v) => {
      setDialog(null);
      setError(null);
      toast(
        v.action === 'withdraw'
          ? t('approvals.withdrawn')
          : r.status === 'approved'
            ? r.direction === 'to_staff'
              ? t('approvals.approvedPosted', { doc: docName(r, t) })
              : t('approvals.approvedAccountant', { doc: docName(r, t) })
            : t('approvals.rejected', { doc: docName(r, t) }),
      );
      await qc.invalidateQueries();
      onDone?.();
    },
    onError: (e) => {
      if (dialog) setError(errorText(e));
      else toast(errorText(e), { tone: 'error' });
    },
  });
  if (!a.canDecide && !a.canWithdraw) return null;
  return (
    <span className="inline-flex items-center gap-2">
      {a.canDecide ? (
        <>
          <Button size={size} onClick={() => setDialog('approve')}>
            {a.direction === 'to_staff' ? t('approvals.approveAndPost') : t('approvals.approve')}
          </Button>
          <Button size={size} variant="outline" onClick={() => setDialog('reject')}>
            {t('approvals.reject')}
          </Button>
        </>
      ) : null}
      {a.canWithdraw ? (
        <Button
          size={size}
          variant="ghost"
          disabled={act.isPending}
          onClick={() => {
            if (window.confirm(t('approvals.withdrawConfirm'))) act.mutate({ action: 'withdraw' });
          }}
        >
          {t('approvals.withdraw')}
        </Button>
      ) : null}
      <DecisionDialog
        open={dialog === 'approve'}
        title={t('approvals.approveTitle', { doc: docName(a, t) })}
        description={
          a.direction === 'to_staff' ? t('approvals.approveStaff') : t('approvals.approveClient')
        }
        confirm={
          a.direction === 'to_staff' ? t('approvals.approveAndPost') : t('approvals.approve')
        }
        destructive={false}
        label={t('approvals.comment')}
        pending={act.isPending}
        error={error}
        onConfirm={(comment) => act.mutate({ action: 'approve', comment })}
        onClose={() => {
          setDialog(null);
          setError(null);
        }}
      />
      <DecisionDialog
        open={dialog === 'reject'}
        title={t('approvals.rejectTitle', { doc: docName(a, t) })}
        description={t('approvals.rejectDescription')}
        confirm={t('approvals.reject')}
        destructive
        required
        label={t('approvals.comment')}
        pending={act.isPending}
        error={error}
        onConfirm={(comment) => act.mutate({ action: 'reject', comment })}
        onClose={() => {
          setDialog(null);
          setError(null);
        }}
      />
    </span>
  );
}
